import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "status", "progress", "results", "error"]
  static values = { 
    companySlug: String,
    reportId: String,
    pollingInterval: { type: Number, default: 5000 }
  }

  connect() {
    console.log('CompetitiveIntelligenceController connected', {
      companySlug: this.companySlugValue,
      hasButtonTarget: this.hasButtonTarget,
      hasStatusTarget: this.hasStatusTarget,
      hasResultsTarget: this.hasResultsTarget,
      element: this.element,
      buttonTarget: this.hasButtonTarget ? this.buttonTarget : null
    })
    this.pollingTimer = null
    this.isStarting = false
    
    // Debug: Check if button has action binding
    if (this.hasButtonTarget) {
      console.log('Button target found:', {
        element: this.buttonTarget,
        hasAction: this.buttonTarget.hasAttribute('data-action'),
        actionValue: this.buttonTarget.getAttribute('data-action')
      })
      
      // Fallback: Add direct event listener if Stimulus action isn't working
      // This helps diagnose if it's a Stimulus registration issue
      const directHandler = (e) => {
        console.log('Direct click handler fired on button')
        // Don't call startWorkflow here - let Stimulus handle it normally
        // This is just for debugging
      }
      this.buttonTarget.addEventListener('click', directHandler, { once: true })
      setTimeout(() => {
        this.buttonTarget.removeEventListener('click', directHandler)
      }, 5000)
    }
    
    // If we have a report ID, start polling
    if (this.reportIdValue) {
      this.startPolling()
    }
  }

  disconnect() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  async startWorkflow(event) {
    // Debug: Log immediately when method is invoked
    console.log('=== startWorkflow invoked ===', {
      event: event ? {
        type: event.type,
        target: event.target,
        currentTarget: event.currentTarget
      } : null,
      hasButtonTarget: this.hasButtonTarget,
      companySlug: this.companySlugValue,
      controllerElement: this.element
    })
    
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    // Prevent double-firing
    if (this.isStarting) {
      console.log('Workflow already starting, ignoring duplicate call')
      return
    }
    this.isStarting = true
    
    // Debug: Log that the method was called
    console.log('startWorkflow method called', {
      hasButtonTarget: this.hasButtonTarget,
      companySlug: this.companySlugValue
    })
    
    // Validate company slug
    if (!this.companySlugValue) {
      console.error('Company slug is required but not found')
      alert('Error: Company slug not found. Please refresh the page.')
      this.isStarting = false
      return
    }
    
    // Use the button target if available, otherwise find the button
    let button
    if (this.hasButtonTarget) {
      button = this.buttonTarget
    } else {
      button = event?.target?.closest('button') || event?.target
    }
    
    if (!button) {
      console.error('Could not find button element')
      alert('Error: Could not find button element. Check console for details.')
      this.isStarting = false
      return
    }
    
    // Check if button is already disabled - if so, something went wrong, re-enable it first
    if (button.disabled) {
      console.warn('Button was already disabled - might be stuck from previous attempt. Re-enabling.')
      button.disabled = false
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      // Restore original button text if we can detect it
      if (!button.innerHTML.includes('🔍')) {
        button.innerHTML = `
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
          🔍 Run Market Intelligence
        `
      }
    }
    
    const originalText = button.innerHTML
    
    // Disable button and show loading
    button.disabled = true
    button.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Starting workflow...
    `
    button.classList.add('opacity-50', 'cursor-not-allowed')

    // Show status section
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove('hidden')
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        throw new Error('CSRF token not found')
      }

      console.log('Making fetch request to:', `/companies/${this.companySlugValue}/reports/search_intelligence`)
      
      const response = await fetch(`/companies/${this.companySlugValue}/reports/search_intelligence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          search_query: "Market intelligence analysis"
        })
      })

      console.log('Response received:', response.status, response.statusText)

      if (!response.ok) {
        let errorData = {}
        try {
          const responseText = await response.text()
          console.log('Response text:', responseText)
          errorData = JSON.parse(responseText)
          console.error('Parsed error data:', errorData)
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError)
          errorData = { error: `HTTP error! status: ${response.status}` }
        }
        
        // Handle validation errors specially (422 status)
        if (response.status === 422 && errorData.status === 'validation_error' && errorData.errors) {
          console.log('Showing validation errors:', errorData.errors)
          this.showValidationErrors(errorData.errors, errorData.message)
          button.disabled = false
          button.innerHTML = originalText
          button.classList.remove('opacity-50', 'cursor-not-allowed')
          this.isStarting = false
          return
        }
        
        // Also check for validation_error status even if status code isn't 422
        if (errorData.status === 'validation_error' && errorData.errors) {
          console.log('Showing validation errors (non-422):', errorData.errors)
          this.showValidationErrors(errorData.errors, errorData.message)
          button.disabled = false
          button.innerHTML = originalText
          button.classList.remove('opacity-50', 'cursor-not-allowed')
          this.isStarting = false
          return
        }
        
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('Response data:', data)

      if (data.workflow_id || data.report_id) {
        this.reportIdValue = data.workflow_id || data.report_id
        this.updateStatus('gathering', 0, 'Workflow started successfully')
        this.startPolling()
        // Keep button disabled since workflow is running
      } else {
        throw new Error(data.error || 'Failed to start workflow - no workflow ID returned')
      }
      
      // Reset flag on success
      this.isStarting = false
    } catch (error) {
      console.error('Error starting workflow:', error)
      this.showError(error.message || 'Failed to start workflow')
      button.disabled = false
      button.innerHTML = originalText
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      this.isStarting = false
    }
  }

  startPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
    }
    
    this.pollingTimer = setInterval(() => {
      this.checkStatus()
    }, this.pollingIntervalValue)

    // Check immediately
    this.checkStatus()
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  async checkStatus() {
    if (!this.reportIdValue) return

    try {
      const response = await fetch(
        `/companies/${this.companySlugValue}/reports/workflow_status?workflow_id=${this.reportIdValue}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      )

      const data = await response.json()

      if (data.status === 'completed') {
        this.handleComplete()
      } else if (data.status === 'failed') {
        this.handleFailed(data.error_message || 'Workflow failed')
      } else {
        // Update progress
        this.updateStatus(
          data.status,
          data.progress || 0,
          this.getStatusMessage(data.status)
        )
      }
    } catch (error) {
      console.error('Error checking status:', error)
      // Don't show error on every poll failure, just log it
    }
  }

  updateStatus(status, progress, message) {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove('hidden')
      this.statusTarget.innerHTML = `
        <div class="flex items-center space-x-3">
          <div class="flex-1">
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm font-medium text-gray-700">${message}</span>
              <span class="text-sm text-gray-500">${progress}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: ${progress}%"></div>
            </div>
          </div>
        </div>
      `
    }

    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${progress}%`
    }
  }

  getStatusMessage(status) {
    const messages = {
      'pending': 'Preparing workflow...',
      'gathering': 'Gathering intelligence data...',
      'analyzing': 'Analyzing and synthesizing...',
      'completed': 'Completed!',
      'failed': 'Workflow failed'
    }
    return messages[status] || 'Processing...'
  }

  async handleComplete() {
    this.stopPolling()
    
    // Fetch report ID to create view link
    let reportId = this.reportIdValue
    
    try {
      const response = await fetch(
        `/companies/${this.companySlugValue}/reports/workflow_results?workflow_id=${this.reportIdValue}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      )

      const data = await response.json()
      
      if (data.status === 'completed') {
        reportId = data.report_id || reportId
        
        // Show completion message with link to view full report
        this.showCompletionMessage(reportId)
      }
    } catch (error) {
      console.error('Error fetching results:', error)
      // Still show completion message even if we can't fetch details
      this.showCompletionMessage(reportId)
    }

    // Re-enable button
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = `
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
        🔍 Run Market Intelligence
      `
      this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  showCompletionMessage(reportId) {
    // Update status to show completion
    this.updateStatus('completed', 100, 'Analysis complete!')
    
    // Show completion message with link to view full report
    if (this.hasResultsTarget) {
      const reportUrl = `/companies/${this.companySlugValue}/reports/${reportId}`
      
      this.resultsTarget.innerHTML = `
        <div class="bg-green-50 border border-green-200 rounded-lg p-6 mt-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <h3 class="text-lg font-semibold text-green-800 mb-2">Report Ready!</h3>
              <p class="text-sm text-green-700 mb-4">
                Your competitive intelligence report has been generated successfully. 
                Click the button below to view the full report with detailed analysis, 
                insights, and recommendations.
              </p>
              <a href="${reportUrl}" 
                 class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                View Full Report
              </a>
            </div>
          </div>
        </div>
      `
      this.resultsTarget.classList.remove('hidden')
    }
  }

  handleFailed(errorMessage) {
    this.stopPolling()
    this.updateStatus('failed', 0, 'Workflow failed')
    this.showError(errorMessage)

    // Re-enable button
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.innerHTML = '🔍 Run Market Intelligence'
      this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }


  showValidationErrors(errors, message) {
    if (!this.hasErrorTarget) return
    
    const errorsList = errors.map(error => {
      const fieldName = error.field ? error.field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Data'
      const fixAction = error.fix_action || 'Edit company to add this information'
      const fixUrl = error.fix_url || '#'
      
      return `
        <li class="mb-2">
          <div class="flex items-start">
            <svg class="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <div class="flex-1">
              <p class="text-sm font-medium text-yellow-800">${this.escapeHtml(error.message)}</p>
              <a href="${fixUrl}" class="text-xs text-indigo-600 hover:text-indigo-800 mt-1 inline-flex items-center">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                ${this.escapeHtml(fixAction)}
              </a>
            </div>
          </div>
        </li>
      `
    }).join('')
    
    this.errorTarget.classList.remove('hidden')
    this.errorTarget.innerHTML = `
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-semibold text-yellow-800 mb-2">Missing Required Data</h3>
            <p class="text-sm text-yellow-700 mb-3">${this.escapeHtml(message || 'Please fix the following issues before running the report:')}</p>
            <ul class="list-none space-y-2">
              ${errorsList}
            </ul>
          </div>
        </div>
      </div>
    `
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
      this.errorTarget.innerHTML = `
        <div class="bg-red-50 border border-red-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">${this.escapeHtml(message)}</p>
            </div>
          </div>
        </div>
      `
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

